package com.zi.support.web

import com.zi.support.shared.Leaderboard
import com.zi.support.shared.LeaderboardWork
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * More > Leaderboard: who closed tickets, finished tasks and subtasks, handled calls and did site visits this week or month (shared Leaderboard
 * scores it), with the team total, the shared 3D podium and a ranked list for each category.
 */
internal object LeaderboardScreen {
    private var work: List<LeaderboardWork>? = null
    private var period = "week"
    private var category = "points"

    fun render() {
        element("content")?.innerHTML = """
          $MORE_BACK
          <div class="page-intro"><div><span class="eyebrow">Team</span><h2>Leaderboard</h2><p>${escape(Leaderboard.SCORING)}</p></div>
            <div class="segmented" id="lb-period" role="group">${Leaderboard.PERIODS.joinToString("") { "<button type=\"button\" data-period=\"$it\">${Leaderboard.periodLabel(it)}</button>" }}</div></div>
          <div id="lb-root"><div class="empty">Loading the leaderboard…</div></div>
        """.trimIndent()
        bindMoreBack()
        document.querySelectorAll("[data-period]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
            period = (e.currentTarget as HTMLElement).getAttribute("data-period") ?: "week"; draw()
        }) }
        work = null; load()
    }

    /** Last month onwards covers this week, this month and the previous of each. */
    private fun load() {
        val now = kotlin.js.Date.now()
        val since = FirebaseGateway.timestampFromMillis(minOf(Leaderboard.previousStart("month", now), Leaderboard.previousStart("week", now)))
        val fs = FirebaseGateway.firestore
        val tickets = fs.getDocs(fs.query(fs.collectionGroup(FirebaseGateway.db, "tickets"), fs.where("completedAt", ">=", since)))
        val calls = fs.getDocs(fs.query(FirebaseGateway.collection(com.zi.support.shared.CallLogPolicy.COLLECTION), fs.where("startedAt", ">=", since)))
        val subtasks = fs.getDocs(fs.query(fs.collectionGroup(FirebaseGateway.db, "subtasks"), fs.where("completedAt", ">=", since)))
        // ponytail: every Kanban task (the board reads the same 5000) and the newest 500 site visits, filtered here; no new indexes.
        val kanbanTasks = fs.getDocs(fs.query(fs.collectionGroup(FirebaseGateway.db, com.zi.support.shared.KanbanPolicy.TASKS), fs.limit(5000)))
        val visits = fs.getDocs(fs.query(fs.collectionGroup(FirebaseGateway.db, "records"), fs.where("dataCategory", "==", "site-visits"), fs.orderBy("createdAt", "desc"), fs.limit(500)))
        promise(js("Promise").all(arrayOf(tickets, calls, subtasks, kanbanTasks, visits)), { snapshots: dynamic ->
            work = records(snapshots[0]).filter { Leaderboard.countsTicket(field(it.data, "status")) }.map { r ->
                LeaderboardWork(field(r.data, "completedByUid"), field(r.data, "completedByUsername"), Leaderboard.TICKET, timestampMillis(r.data.completedAt), field(r.data, "priority"))
            } + records(snapshots[1]).filter { Leaderboard.countsCall(field(it.data, "outcome"), it.data.personal == true, it.data.spam == true) }.map { r ->
                LeaderboardWork(field(r.data, "receivedByUid"), field(r.data, "receivedByName"), Leaderboard.CALL, timestampMillis(r.data.startedAt))
            } + records(snapshots[2]).filter { field(it.data, "status") == "completed" }.map { r ->
                LeaderboardWork(field(r.data, "completedByUid"), field(r.data, "completedByUsername"), Leaderboard.SUBTASK, timestampMillis(r.data.completedAt))
            } + records(snapshots[3]).filter { field(it.data, "status") == "done" }.map { r ->
                val (uid, name) = Leaderboard.taskOwner(field(r.data, "assignedToUid"), field(r.data, "assignedToName"), field(r.data, "completedByUid"), field(r.data, "completedByName"))
                LeaderboardWork(uid, name, Leaderboard.TASK, Leaderboard.taskDoneAt(timestampMillis(r.data.completedAt), timestampMillis(r.data.timerStoppedAt)))
            } + records(snapshots[4]).map { r ->
                LeaderboardWork(field(r.data, "createdByUid"), field(r.data, "visitedBy"), Leaderboard.VISIT, timestampMillis(r.data.createdAt))
            }
            draw()
        }, { message -> element("lb-root")?.innerHTML = "<div class=\"empty\">${escape(message)}</div>" })
    }

    private fun draw() {
        document.querySelectorAll("[data-period]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let {
            it.classList.toggle("active", it.getAttribute("data-period") == period)
        } }
        val all = work ?: return
        val now = kotlin.js.Date.now()
        val board = Leaderboard.build(all, Leaderboard.periodStart(period, now), Leaderboard.previousStart(period, now))
        val ranked = Leaderboard.ranked(board, category)
        val me = FirebaseGateway.currentUser()?.uid as? String ?: ""
        val myRank = ranked.indexOfFirst { it.uid == me }
        val best = ranked.firstOrNull()?.let { Leaderboard.score(it, category) } ?: 1
        val trendUp = board.teamPoints >= board.previousTeamPoints
        element("lb-root")?.innerHTML = """
          <section class="lb-team">
            <div class="lb-team-main"><small>Team ${Leaderboard.periodLabel(period).lowercase()}</small><strong data-count="${board.teamPoints}">0</strong><span>points</span>
              <em class="${if (trendUp) "up" else "down"}"><span class="material-symbols-rounded" aria-hidden="true">${if (trendUp) "trending_up" else "trending_down"}</span>${escape(Leaderboard.trend(board, period))}</em></div>
            <div class="lb-team-stats">
              <div><span class="material-symbols-rounded" aria-hidden="true">confirmation_number</span><b data-count="${board.teamTickets}">0</b><small>tickets closed</small></div>
              <div><span class="material-symbols-rounded" aria-hidden="true">call</span><b data-count="${board.teamCalls}">0</b><small>calls handled</small></div>
              <div><span class="material-symbols-rounded" aria-hidden="true">checklist</span><b data-count="${board.teamTasks + board.teamSubtasks}">0</b><small>tasks done</small></div>
            </div>
          </section>
          <div class="segmented lb-categories" role="group">${Leaderboard.CATEGORIES.joinToString("") { "<button type=\"button\" data-category=\"$it\" class=\"${if (it == category) "active" else ""}\">${Leaderboard.categoryLabel(it)}</button>" }}</div>
          <div class="lb-podium"><iframe id="lb-podium-frame" title="Top three podium"></iframe></div>
          ${if (myRank >= 0) "<p class=\"lb-you\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">person_pin</span>You're <b>#${myRank + 1}</b> of ${ranked.size} for ${Leaderboard.categoryLabel(category).lowercase()} ${Leaderboard.periodLabel(period).lowercase()}.</p>"
            else "<p class=\"lb-you\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">rocket_launch</span>Finish a ticket, task, call or site visit to get on the board.</p>"}
          <ol class="lb-list">${if (ranked.isEmpty()) "<li class=\"empty\">Nothing finished yet ${Leaderboard.periodLabel(period).lowercase()}.</li>" else ranked.mapIndexed { i, e ->
              val score = Leaderboard.score(e, category)
              "<li class=\"${if (e.uid == me) "me" else ""} ${if (i < 3) "top top-${i + 1}" else ""}\" style=\"--i:$i\"><span class=\"lb-rank\">${i + 1}</span>" +
                "<span class=\"lb-avatar\">${escape(Leaderboard.initials(e.name))}</span><span class=\"lb-name\"><strong>${escape(e.name)}${if (e.uid == me) " <small>You</small>" else ""}</strong>" +
                "<small>${escape(Leaderboard.summary(e))}</small><i class=\"lb-bar\"><b style=\"--w:${score * 100 / maxOf(best, 1)}%\"></b></i></span>" +
                "<span class=\"lb-score\">$score<small>${if (category == "points") "pts" else ""}</small></span></li>"
          }.joinToString("")}</ol>
        """.trimIndent()
        document.querySelectorAll("[data-category]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
            category = (e.currentTarget as HTMLElement).getAttribute("data-category") ?: "points"; draw()
        }) }
        element("lb-podium-frame")?.let { showPage(it.asDynamic(), Leaderboard.podiumPage(ranked, category)) }
        countUp()
        // Bars grow in once they're on the page.
        window.requestAnimationFrame { document.querySelector(".lb-list")?.classList?.add("ready") }
    }

    /** Numbers tick up from 0 (skipped when the viewer prefers less motion). */
    private fun countUp() {
        val nodes = document.querySelectorAll("[data-count]")
        val still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        val started = kotlin.js.Date.now()
        fun step() {
            val k = if (still) 1.0 else minOf((kotlin.js.Date.now() - started) / 900.0, 1.0)
            val eased = 1 - (1 - k) * (1 - k) * (1 - k)
            for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { it.textContent = ((it.getAttribute("data-count")?.toIntOrNull() ?: 0) * eased).toInt().toString() }
            if (k < 1) window.requestAnimationFrame { step() }
        }
        step()
    }
}
